import React, { useState, useEffect } from 'react';
import './VenueCreator.css';
import { previewSeatNames, getAlphabetLabel } from '../utils/seatNaming';
import { api } from '../services/api';
import VenueVisualDesigner from './VenueVisualDesigner';
import { toast, confirm as swalConfirm } from '../utils/swal';

const VenueCreator = ({ onVenueCreated, onCancel, editVenue }) => {
  const [venueName, setVenueName] = useState('');
  const [venueType, setVenueType] = useState(0);
  const [stagePosition, setStagePosition] = useState(0);
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState(null);
  const [venueId, setVenueId] = useState('');
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showVisualDesigner, setShowVisualDesigner] = useState(false);

  useEffect(() => {
    if (editVenue) {
      setVenueId(editVenue.id);
      setVenueName(editVenue.name);
      setVenueType(editVenue.type);
      setStagePosition(editVenue.stagePosition);
      setSections(editVenue.sections);
    }
  }, [editVenue]);

  const venueTypes = [
    { value: 0, label: 'Tiyatro',         faIcon: 'fa-masks-theater' },
    { value: 1, label: 'Konser Salonu',   faIcon: 'fa-guitar' },
    { value: 2, label: 'Sinema',          faIcon: 'fa-film' },
    { value: 3, label: 'Stadyum',         faIcon: 'fa-trophy' },
    { value: 4, label: 'Arena',           faIcon: 'fa-building-columns' },
    { value: 5, label: 'Konferans',       faIcon: 'fa-briefcase' }
  ];

  const stagePositions = [
    { value: 0, label: 'Ön Taraf' },
    { value: 1, label: 'Arka Taraf' },
    { value: 2, label: 'Merkez' },
    { value: 3, label: 'Sol Taraf' },
    { value: 4, label: 'Sağ Taraf' }
  ];

  const sectionTypes = [
    { value: 0, label: 'Standart',        color: '#4CAF50' },
    { value: 1, label: 'VIP',             color: '#FFD700' },
    { value: 2, label: 'Balkon',          color: '#2196F3' },
    { value: 3, label: 'Loca',            color: '#E91E63' },
    { value: 4, label: 'Ayakta',          color: '#FF9800' },
    { value: 5, label: 'Engelli Koltuğu', color: '#9C27B0' }
  ];

  const templates = [
    {
      name: 'Klasik Tiyatro', type: 0, stagePosition: 0,
      sections: [
        { name: 'Ön Sıra (VIP)', type: 1, startRow: 1, endRow: 3, seatsPerRow: 10, rowPrefix: 'V' },
        { name: 'Orta Bölüm',   type: 0, startRow: 4, endRow: 11, seatsPerRow: 12, rowPrefix: 'A' },
        { name: 'Balkon',        type: 2, startRow: 12, endRow: 16, seatsPerRow: 8, rowPrefix: 'BAL' }
      ]
    },
    {
      name: 'Amfitiyatro', type: 0, stagePosition: 2,
      sections: [
        { name: 'VIP Bölüm',      type: 1, startRow: 1, endRow: 3,  seatsPerRow: 15, rowPrefix: 'V' },
        { name: 'Standart Bölüm', type: 0, startRow: 4, endRow: 13, seatsPerRow: 20, rowPrefix: 'S' },
        { name: 'Arka Bölüm',     type: 0, startRow: 14, endRow: 18, seatsPerRow: 25, rowPrefix: 'R' }
      ]
    }
  ];

  const openVisualDesigner = () => {
    if (sections.length === 0) { toast.warning('Önce en az bir bölüm ekleyin!'); return; }
    setShowVisualDesigner(true);
  };

  const handleVisualDesignerSave = async (updatedVenue) => {
    setSections(updatedVenue.sections);
    setShowVisualDesigner(false);
    const venueData = {
      name: venueName, type: venueType, stagePosition,
      sections: updatedVenue.sections.map(s => ({
        id: s.id, name: s.name, type: s.type,
        startRow: s.startRow, endRow: s.endRow, seatsPerRow: s.seatsPerRow,
        seatStartNumber: s.seatStartNumber ?? 1,
        reverseSeats: s.reverseSeats ?? false,
        rowPrefix: s.rowPrefix, namingMode: s.namingMode || 0,
        position: s.position || null,
        rowSeats: s.rowSeats && Object.keys(s.rowSeats).length > 0 ? s.rowSeats : null
      }))
    };
    try {
      let response;
      if (editVenue || venueId) {
        response = await api.updateVenue(venueId || editVenue?.id, venueData);
      } else {
        response = await api.createVenue(venueData);
      }
      if (!response || response instanceof Response && !response.ok) throw new Error('Kayıt başarısız');
      setVenueId(response.id);
      toast.success('Görsel tasarım kaydedildi!');
    } catch (error) {
      toast.error(`Hata: ${error.message}`);
    }
  };

  const validateSection = (section, excludeIndex = null) => {
    const errors = [];
    if (!section.name.trim()) errors.push('Bölüm adı boş olamaz');
    if (!section.rowPrefix.trim()) errors.push('Sıra prefixi boş olamaz');
    if (section.startRow < 0) errors.push('Başlangıç sırası 0 veya daha büyük olmalı');
    if (section.endRow < section.startRow) errors.push('Bitiş sırası, başlangıç sırasından küçük olamaz');
    if (section.seatsPerRow < 1) errors.push('Sıra başına en az 1 koltuk olmalı');
    const namingMode = section.namingMode || 0;
    sections.forEach((existingSection, index) => {
      if (excludeIndex !== null && index === excludeIndex) return;
      if (namingMode === 1 && existingSection.namingMode === 1) {
        if (section.rowPrefix === existingSection.rowPrefix) {
          const newRange = { start: section.startRow, end: section.endRow };
          const existingRange = { start: existingSection.startRow, end: existingSection.endRow };
          if (!(newRange.end < existingRange.start || newRange.start > existingRange.end)) {
            errors.push(`"${section.rowPrefix}" prefixi "${existingSection.name}" bölümünde zaten kullanılıyor!`);
          }
        }
      }
    });
    return errors;
  };

  const loadTemplate = (template) => {
    setVenueName(template.name);
    setVenueType(template.type);
    setStagePosition(template.stagePosition);
    setSections(template.sections.map(s => ({ ...s, id: `SECTION_${Date.now()}_${Math.random()}` })));
  };

  const addSection = () => {
    setCurrentSection({ id: `SECTION_${Date.now()}`, name: '', type: 0, startRow: 1, endRow: 5, seatsPerRow: 10, seatStartNumber: 1, reverseSeats: false, rowPrefix: 'A', namingMode: 0 });
    setEditingSectionIndex(null);
    setValidationErrors([]);
  };

  const editSection = (index) => {
    setCurrentSection({ ...sections[index] });
    setEditingSectionIndex(index);
    setValidationErrors([]);
  };

  const saveSection = () => {
    if (!currentSection) return;
    const errors = validateSection(currentSection, editingSectionIndex);
    if (errors.length > 0) { setValidationErrors(errors); return; }
    if (editingSectionIndex !== null) {
      const updated = [...sections];
      updated[editingSectionIndex] = currentSection;
      setSections(updated);
    } else {
      setSections([...sections, currentSection]);
    }
    setCurrentSection(null);
    setValidationErrors([]);
  };

  const deleteSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!venueName.trim()) { toast.error('Salon adı zorunludur!'); return; }
    if (sections.length === 0) { toast.error('En az bir bölüm ekleyin!'); return; }

    const venueData = {
      name: venueName, type: venueType, stagePosition,
      sections: sections.map(s => ({
        id: s.id, name: s.name, type: s.type,
        startRow: s.startRow, endRow: s.endRow, seatsPerRow: s.seatsPerRow,
        seatStartNumber: s.seatStartNumber ?? 1,
        reverseSeats: s.reverseSeats ?? false,
        rowPrefix: s.rowPrefix, namingMode: s.namingMode || 0,
        position: s.position || null,
        rowSeats: s.rowSeats && Object.keys(s.rowSeats).length > 0 ? s.rowSeats : null
      }))
    };

    try {
      let response;
      if (editVenue || venueId) {
        response = await api.updateVenue(venueId || editVenue?.id, venueData);
      } else {
        response = await api.createVenue(venueData);
      }
      if (!response || response instanceof Response && !response.ok) throw new Error('İşlem başarısız');
      toast.success(editVenue ? 'Salon güncellendi!' : 'Salon oluşturuldu!');
      onVenueCreated(response);
    } catch (error) {
      toast.error(`Hata: ${error.message}`);
    }
  };

  const stats = {
    totalCapacity: sections.reduce((sum, s) => sum + ((s.endRow - s.startRow + 1) * s.seatsPerRow), 0)
  };

  return (
    <div className="venue-creator">
      <div className="creator-header">
        <h2>
          <i className={`fa-solid ${editVenue ? 'fa-pen' : 'fa-plus'}`} />{' '}
          {editVenue ? 'Salon Düzenle' : 'Yeni Salon Oluştur'}
        </h2>
      </div>

      {/* Şablonlar */}
      {!editVenue && (
        <div className="templates-section">
          <h3><i className="fa-solid fa-copy" /> Hazır Şablonlar</h3>
          <div className="templates-grid">
            {templates.map((template, i) => (
              <button key={i} onClick={() => loadTemplate(template)} className="template-btn">
                <i className="fa-solid fa-masks-theater" /> {template.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Temel bilgiler */}
        <div className="form-section">
          <h3><i className="fa-solid fa-building" /> Temel Bilgiler</h3>

          <div className="form-group">
            <label>Salon Adı *</label>
            <input type="text" value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder="Salon adını girin" required />
          </div>

          <div className="form-group">
            <label>Salon Tipi</label>
            <div className="venue-type-grid">
              {venueTypes.map(type => (
                <button
                  key={type.value}
                  type="button"
                  className={`venue-type-btn ${venueType === type.value ? 'active' : ''}`}
                  onClick={() => setVenueType(type.value)}
                >
                  <i className={`fa-solid ${type.faIcon}`} /> {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Sahne Konumu</label>
            <select value={stagePosition} onChange={(e) => setStagePosition(parseInt(e.target.value))}>
              {stagePositions.map(pos => (
                <option key={pos.value} value={pos.value}>{pos.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bölümler */}
        <div className="form-section">
          <div className="section-header">
            <h3><i className="fa-solid fa-table-cells" /> Bölümler</h3>
            <div className="section-header-actions">
              {sections.length > 0 && (
                <button type="button" onClick={openVisualDesigner} className="visual-designer-btn">
                  <i className="fa-solid fa-pen-ruler" /> Görsel Tasarımcı
                </button>
              )}
              <button type="button" onClick={addSection} className="add-section-btn">
                <i className="fa-solid fa-plus" /> Bölüm Ekle
              </button>
            </div>
          </div>

          {sections.length === 0 ? (
            <div className="no-sections">
              <i className="fa-solid fa-chair" /> Henüz bölüm eklenmedi.
            </div>
          ) : (
            <div className="sections-list">
              {sections.map((section, index) => (
                <div key={section.id || index} className="section-item">
                  <div className="section-info">
                    <span className="section-name">{section.name}</span>
                    <span className="section-type">{sectionTypes[section.type]?.label}</span>
                    <span className="section-capacity">
                      {(section.endRow - section.startRow + 1) * section.seatsPerRow} koltuk
                    </span>
                  </div>
                  <div className="section-actions">
                    <button type="button" onClick={() => editSection(index)} className="edit-btn" title="Düzenle">
                      <i className="fa-solid fa-pen" />
                    </button>
                    <button type="button" onClick={() => deleteSection(index)} className="delete-btn" title="Sil">
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Özet */}
        <div className="summary-section">
          <h3><i className="fa-solid fa-chart-bar" /> Özet</h3>
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Toplam Kapasite:</span>
              <span className="stat-value">{stats.totalCapacity} koltuk</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Bölüm Sayısı:</span>
              <span className="stat-value">{sections.length} bölüm</span>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-btn">
            <i className="fa-solid fa-xmark" /> İptal
          </button>
          <button type="submit" className="submit-btn">
            <i className={`fa-solid ${editVenue ? 'fa-floppy-disk' : 'fa-plus'}`} />{' '}
            {editVenue ? 'Güncelle' : 'Salon Oluştur'}
          </button>
        </div>
      </form>

      {/* Bölüm Modal */}
      {currentSection && (
        <div className="modal-overlay" onClick={() => setCurrentSection(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className={`fa-solid ${editingSectionIndex !== null ? 'fa-pen' : 'fa-plus'}`} />{' '}
                {editingSectionIndex !== null ? 'Bölüm Düzenle' : 'Yeni Bölüm Ekle'}
              </h3>
              <button onClick={() => setCurrentSection(null)} className="modal-close">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="modal-body">
              {validationErrors.length > 0 && (
                <div className="validation-errors">
                  <strong><i className="fa-solid fa-triangle-exclamation" /> Dikkat:</strong>
                  <ul>{validationErrors.map((error, i) => <li key={i}>{error}</li>)}</ul>
                </div>
              )}

              <div className="form-group">
                <label>Numaralandırma Modu *</label>
                <select
                  value={currentSection.namingMode || 0}
                  onChange={(e) => {
                    const mode = parseInt(e.target.value);
                    setCurrentSection({ ...currentSection, namingMode: mode, rowPrefix: mode === 0 ? '' : currentSection.rowPrefix });
                  }}
                >
                  <option value={0}>Sürekli Alfabe (A, B, C, D...)</option>
                  <option value={1}>Özel İsimlendirme (VIP1-5, BALKON3-12)</option>
                  <option value={2}>Manuel (Her sırayı elle adlandır)</option>
                </select>
                <small className="hint">
                  {(currentSection.namingMode || 0) === 0 && 'Tüm salon tek alfabe ile ilerler (1→A, 2→B, 27→AA)'}
                  {currentSection.namingMode === 1 && 'Her bölüm kendi ismini taşır (VIP1, VIP2, BALKON1...)'}
                  {currentSection.namingMode === 2 && 'Her sırayı kendiniz adlandırabilirsiniz'}
                </small>
              </div>

              <div className="form-group">
                <label>Bölüm Adı *</label>
                <input type="text" value={currentSection.name}
                  onChange={(e) => setCurrentSection({ ...currentSection, name: e.target.value })}
                  placeholder="Örn: Ön Sıra, VIP Bölüm" />
              </div>

              <div className="form-group">
                <label>Bölüm Tipi *</label>
                <select value={currentSection.type}
                  onChange={(e) => setCurrentSection({ ...currentSection, type: parseInt(e.target.value) })}>
                  {sectionTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </div>

              {currentSection.namingMode !== 0 && (
                <div className="form-group">
                  <label>Bölüm Prefixi *</label>
                  <input type="text" value={currentSection.rowPrefix || ''}
                    onChange={(e) => setCurrentSection({ ...currentSection, rowPrefix: e.target.value.toUpperCase() })}
                    placeholder="VIP, BALKON, LOCA vb." maxLength="10" />
                  <small className="hint">Koltuk isimlerinde kullanılacak ön ek</small>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Başlangıç Sırası *</label>
                  <input type="number" value={currentSection.startRow || 1}
                    onChange={(e) => setCurrentSection({ ...currentSection, startRow: Math.max(1, parseInt(e.target.value) || 1) })}
                    min="1" />
                  <small className="hint">
                    {currentSection.namingMode === 0 ? '1 = A, 2 = B, 27 = AA' : 'Başlangıç sıra numarası'}
                  </small>
                </div>
                <div className="form-group">
                  <label>Bitiş Sırası *</label>
                  <input type="number" value={currentSection.endRow || 5}
                    onChange={(e) => setCurrentSection({ ...currentSection, endRow: Math.max(currentSection.startRow || 1, parseInt(e.target.value) || 5) })}
                    min={currentSection.startRow || 1} />
                  <small className="hint">
                    {currentSection.namingMode === 0 && currentSection.endRow
                      ? `${getAlphabetLabel(currentSection.endRow)} sırasına kadar`
                      : 'Son sıra numarası'}
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>Sıra Başına Koltuk Sayısı *</label>
                <input type="number" value={currentSection.seatsPerRow}
                  onChange={(e) => setCurrentSection({ ...currentSection, seatsPerRow: parseInt(e.target.value) || 1 })}
                  min="1" />
              </div>

              <div className="form-group">
                <label>Koltuk Numarası Başlangıcı</label>
                <input type="number" value={currentSection.seatStartNumber ?? 1}
                  onChange={(e) => setCurrentSection({ ...currentSection, seatStartNumber: parseInt(e.target.value) || 1 })}
                  min="1" />
                <small className="hint">Sol blok: 1, Orta blok: 9, Sağ blok: 25 gibi</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={currentSection.reverseSeats ?? false}
                    onChange={(e) => setCurrentSection({ ...currentSection, reverseSeats: e.target.checked })} />
                  <span>Sağdan sola numaralandır (büyükten küçüğe)</span>
                </label>
              </div>

              <div className="section-preview">
                <strong><i className="fa-solid fa-eye" /> Önizleme:</strong>
                <pre>{previewSeatNames(currentSection).join('\n')}</pre>
                <p><strong>Toplam:</strong> {(currentSection.endRow - currentSection.startRow + 1) * currentSection.seatsPerRow} koltuk</p>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setCurrentSection(null)} className="cancel-btn">
                <i className="fa-solid fa-xmark" /> İptal
              </button>
              <button onClick={saveSection} className="save-btn">
                <i className="fa-solid fa-floppy-disk" /> Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Görsel Tasarımcı Modal */}
      {showVisualDesigner && (
        <VenueVisualDesigner
          venue={{ name: venueName, sections }}
          onSave={handleVisualDesignerSave}
          onCancel={() => setShowVisualDesigner(false)}
        />
      )}
    </div>
  );
};

export default VenueCreator;
